/**
 * UserMultiPicker — Wave 3 Batch J. Linear-style multi-select dropdown
 * for picking users from the project member list. Used for:
 *   - Event Attendees (legacy "Attendees" field on EventDetails)
 *   - Announcement Collaborators (legacy `collaborators_ids` field)
 *
 * Trigger looks like a multi-select input — bordered container with
 * chips for each selected user and a caret. Click opens a checkbox list
 * over the project members.
 *
 * UX is deliberately simpler than `<LabelPicker>` (no inline-create, no
 * keyboard listbox model) — for a v1 of this feature the parent calls
 * `onChange` with the FULL new id set; the parent decides whether to
 * fire one bulk-replace mutation or per-toggle calls. Defer keyboard
 * a11y polish to a follow-up.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { ProjectMember } from '@/api/types-bridge'
import { AgentModelLine, AiTag, displayEmail, isAgentKind } from '@/components/AiTag'

interface UserMultiPickerProps {
  members: readonly ProjectMember[]
  selectedUserIds: readonly string[]
  onChange: (next: string[]) => void
  placeholder?: string
  /**
   * Drop AI agents from the options and the chips. Agents are eligible by
   * default (they are teammates); a form whose action genuinely needs a
   * person (an accountable owner, signing off) opts in.
   */
  humansOnly?: boolean
}

export function UserMultiPicker({
  members: allMembers,
  selectedUserIds,
  onChange,
  placeholder,
  humansOnly = false,
}: UserMultiPickerProps) {
  const { t } = useTranslation('board')
  const resolvedPlaceholder = placeholder ?? t('userPicker.addPlaceholder')
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [open, setOpen] = useState(false)

  const members = useMemo(
    () => (humansOnly ? allMembers.filter((m) => !isAgentKind(m.kind)) : allMembers),
    [allMembers, humansOnly],
  )
  // Ids that are filtered out of view. A humans-only form must not carry
  // them forward in what it emits: they are invisible, so the user could
  // neither see nor remove them, yet they would still be submitted.
  const hiddenIds = useMemo(
    () =>
      humansOnly
        ? new Set(allMembers.filter((m) => isAgentKind(m.kind)).map((m) => m.userId))
        : new Set<string>(),
    [allMembers, humansOnly],
  )
  const emit = (next: readonly string[]) => onChange(next.filter((id) => !hiddenIds.has(id)))
  const selectedSet = useMemo(() => new Set(selectedUserIds), [selectedUserIds])
  const selected = useMemo(
    () => members.filter((m) => selectedSet.has(m.userId)),
    [members, selectedSet],
  )

  useEffect(() => {
    if (!open) return
    const onMouseDown = (event: globalThis.MouseEvent) => {
      if (!containerRef.current) return
      if (!containerRef.current.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    return () => document.removeEventListener('mousedown', onMouseDown)
  }, [open])

  const toggle = (userId: string) => {
    const next = selectedSet.has(userId)
      ? selectedUserIds.filter((id) => id !== userId)
      : [...selectedUserIds, userId]
    emit(next)
  }

  const remove = (userId: string) => {
    emit(selectedUserIds.filter((id) => id !== userId))
  }

  return (
    <div
      ref={containerRef}
      className="relative"
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.preventDefault()
          setOpen(false)
        }
      }}
    >
      <div
        // biome-ignore lint/a11y/useSemanticElements: combobox trigger that wraps user-chip removable buttons — a native <button> can't contain other <button>s (invalid HTML), so we keep the role-bearing <div>.
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setOpen((v) => !v)
          }
        }}
        className="flex min-h-[2.25rem] w-full cursor-pointer flex-wrap items-center gap-1 rounded-md border border-border-strong bg-bg-elevated px-2 py-1 text-sm text-fg shadow-sm focus-within:border-fg focus-within:ring-1 focus-within:ring-fg hover:bg-bg-muted"
      >
        {selected.length === 0 && <span className="ms-1 text-fg-muted">{resolvedPlaceholder}</span>}
        {selected.map((m) => (
          <span
            key={m.userId}
            className="inline-flex max-w-[14rem] items-center gap-1 rounded bg-bg-muted px-1.5 py-0.5 text-xs font-medium text-fg"
          >
            <span className="truncate" title={displayEmail(m) ?? undefined}>
              {m.name || m.email}
            </span>
            <AiTag kind={m.kind} userId={m.userId} />
            <button
              type="button"
              aria-label={t('userPicker.remove', { name: m.name || m.email })}
              onClick={(event) => {
                event.stopPropagation()
                remove(m.userId)
              }}
              className="-me-0.5 inline-flex h-3.5 w-3.5 flex-none items-center justify-center rounded-full text-fg-muted hover:bg-slate-300 hover:text-fg"
            >
              <svg
                width="9"
                height="9"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <title>{t('userPicker.removeIconTitle')}</title>
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
          </span>
        ))}
        <span className="ms-auto flex-none text-fg-subtle" aria-hidden="true">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            className={`transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <title>{t('userPicker.toggleMenuTitle')}</title>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>

      {open && (
        <div className="absolute start-0 end-0 z-40 mt-1 max-h-72 overflow-auto rounded-md border border-border-strong bg-bg-elevated py-1 shadow-lg">
          <div className="px-3 pb-1 pt-2 text-xs font-semibold text-fg-muted">
            {t('userPicker.projectMembersHeading')}
          </div>
          {members.length === 0 && (
            <div className="px-3 py-2 text-sm text-fg-subtle">{t('userPicker.noMembers')}</div>
          )}
          {members.map((m) => {
            const checked = selectedSet.has(m.userId)
            return (
              <button
                key={m.userId}
                type="button"
                onClick={() => toggle(m.userId)}
                className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-fg hover:bg-bg-muted"
              >
                <span
                  aria-hidden="true"
                  className={`flex h-4 w-4 flex-none items-center justify-center rounded border ${
                    checked
                      ? 'border-fg bg-accent text-accent-fg'
                      : 'border-border-strong bg-bg-elevated'
                  }`}
                >
                  {checked && (
                    <svg
                      width="10"
                      height="10"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <title>{t('userPicker.selectedIconTitle')}</title>
                      <path d="M5 12l5 5L20 7" />
                    </svg>
                  )}
                </span>
                <div className="flex min-w-0 flex-col">
                  <span className="flex min-w-0 items-center gap-1.5">
                    <span className="truncate">{m.name || m.email}</span>
                    <AiTag kind={m.kind} userId={m.userId} />
                  </span>
                  {m.name && displayEmail(m) && m.name !== m.email ? (
                    <span className="truncate text-xs text-fg-muted">{displayEmail(m)}</span>
                  ) : m.kind === 'agent' ? (
                    <AgentModelLine userId={m.userId} className="truncate text-xs text-fg-muted" />
                  ) : null}
                </div>
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}
